import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField as MuiTextField,
  Button,
  Box,
  Breadcrumbs,
  Link,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
  LinearProgress,
  FormLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormGroup,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import { useParams, NavLink } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  changeUserPassword,
  fetchUserById,
  InvoiceDeliveryMethod,
  updateUser,
} from "../../redux/slices/user";
import useAuth from "../../hooks/useAuth";
import styled from "@emotion/styled";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";
import { UserPermissionsView } from "../../components/members/UserPermissionsView";
import { RoleChip } from "../../components/badges/RoleChip";
import { ProtectedUserBadge } from "../../components/badges/ProtectedUserBadge";
import { SuperAdminBadge } from "../../components/badges/SuperAdminBadge";
import { usePreviewPermissions } from "../../hooks/usePermission";
import { updateUserAssociation } from "../../redux/slices/userAssociation";

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const userSchema = yup.object().shape({
  firstName: yup.string().required("Etunimi on pakollinen"),
  lastName: yup.string().required("Sukunimi on pakollinen"),
  email: yup
    .string()
    .email("Virheellinen sähköposti")
    .required("Sähköposti vaaditaan"),
  phone: yup
    .string()
    .matches(/^[0-9]{10,}$/, "Anna voimassa oleva puhelinnumero")
    .required("Puhelinnumero vaaditaan"),
  street: yup.string().required("Katuosoite on pakollinen"),
  city: yup.string().required("Kaupunki tai kunta vaaditaan"),
  postalCode: yup
    .string()
    .matches(/^\d{5}$/, "Postinumero tulee olla 5 numeroa")
    .required("Postinumero vaaditaan"),
  invoiceEmail: yup.string().nullable().email("Anna oikea sähköpostiosoite"),
  invoiceDeliveryMethod: yup
    .mixed<InvoiceDeliveryMethod>()
    .oneOf([InvoiceDeliveryMethod.EMAIL, InvoiceDeliveryMethod.POST]),
});

const passwordSchema = yup.object().shape({
  currentPassword: yup.string().required("Nykyinen salasana vaaditaan"),
  newPassword: yup
    .string()
    .required("Uusi salasana vaaditaan")
    .min(8, "Vähintään 8 merkkiä")
    .matches(/[A-Z]/, "Vähintään yksi iso kirjain")
    .matches(/[a-z]/, "Vähintään yksi pieni kirjain")
    .matches(/\d/, "Vähintään yksi numero")
    .matches(/[@$!%*?#&]/, "Vähintään yksi erikoismerkki"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Salasanat eivät täsmää")
    .required("Vahvista salasana"),
});

const SettingsPage = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const user = useAppSelector((state) => state.user.user);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Check if current user has ADMIN role (can edit own roles)
  const isAdmin = currentUser?.activeAssociation?.roles?.includes("ADMIN");

  // Preview permissions based on selected roles
  const { permissions: previewPermissions, loading: previewLoading } =
    usePreviewPermissions(associationId, selectedRoles);

  useEffect(() => {
    if (id && associationId) {
      dispatch(fetchUserById({ id, associationId }));
    }
  }, [dispatch, id, associationId]);

  // Initialize selected roles from current user's active association
  useEffect(() => {
    if (currentUser?.activeAssociation?.roles) {
      setSelectedRoles(currentUser.activeAssociation.roles);
    }
  }, [currentUser?.activeAssociation?.roles]);

  const handleRoleToggle = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleSaveRoles = async () => {
    if (!currentUser || !associationId) return;

    setSavingRoles(true);
    try {
      await dispatch(
        updateUserAssociation({
          userId: currentUser.id,
          associationId,
          updates: {
            roles: selectedRoles,
          },
        })
      ).unwrap();

      setSnackbarMessage(
        "Roolit tallennettu! Päivitä sivu nähdäksesi muutokset."
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setProgress(100);
    } catch (error: any) {
      console.error("Error saving roles:", error);
      // Extract error message from backend response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Virhe roolien tallentamisessa.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setProgress(100);
    } finally {
      setSavingRoles(false);
    }
  };

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  useEffect(() => {
    if (snackbarOpen) {
      const interval = 100;
      const decrement = 100 / (duration / interval);

      const timer = setInterval(() => {
        setProgress((old) => {
          const updated = Math.max(old - decrement, 0);
          if (updated === 0) {
            clearInterval(timer);
            setSnackbarOpen(false);
          }
          return updated;
        });
      }, interval);

      return () => clearInterval(timer);
    }
  }, [snackbarOpen]);

  if (!user || !currentUser || user.id !== currentUser.id) {
    return (
      <>
        <Helmet title="Asetukset" />
        <Typography variant="h3">Asetukset</Typography>
        <Breadcrumbs sx={{ mt: 2 }}>
          <Link component={NavLink} to="/">
            Etusivu
          </Link>
          <Typography>Asetukset</Typography>
        </Breadcrumbs>
        <Divider sx={{ my: 4 }} />
        <Typography color="error">
          Sinulla ei ole oikeuksia nähdä tätä profiilia.
        </Typography>
      </>
    );
  }

  return (
    <>
      <Helmet title="Asetukset" />
      <Typography variant="h3">Asetukset</Typography>
      <Breadcrumbs sx={{ mt: 2 }}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Asetukset</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 4 }} />

      {/* Käyttäjäprofiilin lomake */}
      <Formik
        initialValues={{
          firstName: user.firstName ?? "",
          lastName: user.lastName ?? "",
          email: user.email ?? "",
          phone: user.phone ?? "",
          street: user.address?.street ?? "",
          city: user.address?.city ?? "",
          postalCode: user.address?.postalCode ?? "",
          invoiceEmail: user.invoiceEmail ?? "",
          invoiceDeliveryMethod:
            user.invoiceDeliveryMethod ?? InvoiceDeliveryMethod.EMAIL,
        }}
        validationSchema={userSchema}
        onSubmit={(values) => {
          const associationId =
            currentUser?.activeAssociation?.id ||
            currentUser?.associations?.[0]?.id;
          dispatch(
            updateUser({
              id: user.id,
              data: values,
              associationId,
            })
          );
          setSnackbarMessage("Asetukset tallennettu!");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
          setProgress(100);
        }}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched,
        }) => (
          <form onSubmit={handleSubmit}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6">Käyttäjäprofiili</Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={6}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="firstName"
                      label="Etunimi"
                      fullWidth
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.firstName && errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="lastName"
                      label="Sukunimi"
                      fullWidth
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.lastName && errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="email"
                      label="Sähköposti"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      disabled
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="phone"
                      label="Puhelinnumero"
                      fullWidth
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="street"
                      label="Katuosoite"
                      fullWidth
                      value={values.street}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.street && errors.street)}
                      helperText={touched.street && errors.street}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="postalCode"
                      label="Postinumero"
                      fullWidth
                      value={values.postalCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.postalCode && errors.postalCode)}
                      helperText={touched.postalCode && errors.postalCode}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="city"
                      label="Kaupunki / Kunta"
                      fullWidth
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormLabel component="legend">Laskutustapa</FormLabel>
                    <RadioGroup
                      name="invoiceDeliveryMethod"
                      row
                      value={values.invoiceDeliveryMethod}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="EMAIL"
                        control={<Radio />}
                        label="Sähköposti"
                      />
                      <FormControlLabel
                        value="POST"
                        control={<Radio />}
                        label="Posti"
                      />
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="invoiceEmail"
                      label="Laskutussähköposti"
                      placeholder="esim. naapuri@gmail.com"
                      fullWidth
                      value={values.invoiceEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.invoiceEmail && errors.invoiceEmail
                      )}
                      helperText={
                        (touched.invoiceEmail && errors.invoiceEmail) ||
                        "Jos asetettu, laskut ja kokouskutsut lähetetään tähän osoitteeseen"
                      }
                      type="email"
                      my={2}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4 }}>
                  <Button variant="contained" type="submit">
                    Tallenna
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </form>
        )}
      </Formik>

      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        validationSchema={passwordSchema}
        onSubmit={async ({ currentPassword, newPassword }, actions) => {
          try {
            const associationId =
              currentUser?.activeAssociation?.id ||
              currentUser?.associations?.[0]?.id;
            const result = await dispatch(
              changeUserPassword({
                id,
                currentPassword,
                newPassword,
                associationId,
              })
            ).unwrap();

            if (result === true || result?.success === true) {
              actions.resetForm();
              setProgress(100);
              setSnackbarMessage("Salasana vaihdettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              throw new Error("UNKNOWN_ERROR");
            }

            setOpenSnackbar(true);
          } catch (error) {
            setProgress(100);
            setSnackbarMessage("Tapahtui virhe salasanan vaihtamisessa.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
          }
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched,
        }) => {
          const passwordFields: (keyof typeof values)[] = [
            "currentPassword",
            "newPassword",
            "confirmPassword",
          ];

          return (
            <form onSubmit={handleSubmit}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Vaihda salasana</Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    {passwordFields.map((field) => (
                      <Grid item xs={12} key={field}>
                        <TextField
                          fullWidth
                          name={field}
                          label={
                            field === "currentPassword"
                              ? "Nykyinen salasana"
                              : field === "newPassword"
                              ? "Uusi salasana"
                              : "Vahvista uusi salasana"
                          }
                          type="password"
                          value={values[field]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched[field] && errors[field])}
                          helperText={touched[field] && errors[field]}
                          autoComplete="off"
                          my={2}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" type="submit">
                      Vaihda salasana
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </form>
          );
        }}
      </Formik>

      {/* Associations & Roles Section */}
      <Card sx={{ mt: 4, mb: 4 }}>
        <CardContent>
          <Typography variant="h6">Jäsenyydet ja roolit</Typography>
          <Divider sx={{ my: 2 }} />

          {/* User badges */}
          <Box sx={{ mb: 3, display: "flex", gap: 1 }}>
            {user.isSuperAdmin && <SuperAdminBadge />}
            {user.isProtected && (
              <ProtectedUserBadge protectionReason={user.protectionReason} />
            )}
          </Box>

          {currentUser.associations && currentUser.associations.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tiekunta</TableCell>
                    <TableCell>Roolit</TableCell>
                    <TableCell>Tila</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentUser.associations.map((assoc: any) => (
                    <TableRow key={assoc.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {assoc?.name || "Tuntematon"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {assoc.roles && assoc.roles.length > 0 ? (
                            assoc.roles.map((role: string) => (
                              <RoleChip key={role} role={role} size="small" />
                            ))
                          ) : (
                            <Chip label="Ei roolia" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            assoc.status?.toLowerCase() === "active" ||
                            assoc.status === "ACTIVE"
                              ? "Aktiivinen"
                              : assoc.status?.toLowerCase() === "suspended" ||
                                assoc.status === "SUSPENDED"
                              ? "Jäädytetty"
                              : assoc.status?.toLowerCase() === "inactive" ||
                                assoc.status === "INACTIVE"
                              ? "Ei-aktiivinen"
                              : assoc.status
                          }
                          size="small"
                          color={
                            assoc.status?.toLowerCase() === "active" ||
                            assoc.status === "ACTIVE"
                              ? "success"
                              : assoc.status?.toLowerCase() === "suspended" ||
                                assoc.status === "SUSPENDED"
                              ? "error"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">Et ole jäsen missään tiekunnassa.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Role Management Section - Only for active association */}
      {currentUser.activeAssociation?.id && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Roolien hallinta - {currentUser.activeAssociation?.name}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {isAdmin ? (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Voit muokata omia roolejasi tässä tiekunnassa.
                </Alert>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" sx={{ mb: 2 }}>
                        Roolit
                      </FormLabel>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedRoles.includes("ADMIN")}
                              onChange={() => handleRoleToggle("ADMIN")}
                            />
                          }
                          label="Ylläpitäjä (ADMIN)"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedRoles.includes("ACCOUNTANT")}
                              onChange={() => handleRoleToggle("ACCOUNTANT")}
                            />
                          }
                          label="Kirjanpitäjä (ACCOUNTANT)"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedRoles.includes("MEMBER")}
                              onChange={() => handleRoleToggle("MEMBER")}
                            />
                          }
                          label="Osakas (MEMBER)"
                        />
                        {currentUser?.isSuperAdmin && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedRoles.includes("CONTRACTOR")}
                                onChange={() => handleRoleToggle("CONTRACTOR")}
                              />
                            }
                            label="Urakoitsija (CONTRACTOR)"
                          />
                        )}
                        {currentUser?.isSuperAdmin && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedRoles.includes("GOVERNMENT")}
                                onChange={() => handleRoleToggle("GOVERNMENT")}
                              />
                            }
                            label="Viranomainen (GOVERNMENT)"
                          />
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedRoles.includes("GUEST")}
                              onChange={() => handleRoleToggle("GUEST")}
                            />
                          }
                          label="Vieras (GUEST)"
                        />
                      </FormGroup>
                      {selectedRoles.length === 0 && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          Valitse vähintään yksi rooli
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveRoles}
                    disabled={savingRoles || selectedRoles.length === 0}
                  >
                    {savingRoles ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Tallenna roolit"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (currentUser?.activeAssociation?.roles) {
                        setSelectedRoles(currentUser.activeAssociation.roles);
                      }
                    }}
                    disabled={savingRoles}
                  >
                    Peruuta
                  </Button>
                </Box>
              </>
            ) : (
              <Alert severity="info">
                Vain ylläpitäjät (ADMIN) voivat muokata omia roolejaan. Nykyiset
                roolisi:{" "}
                {currentUser.activeAssociation.roles
                  ?.map((r: string) => {
                    const labels: Record<string, string> = {
                      ADMIN: "Ylläpitäjä",
                      ACCOUNTANT: "Kirjanpitäjä",
                      MEMBER: "Osakas",
                      CONTRACTOR: "Urakoitsija",
                      GOVERNMENT: "Viranomainen",
                      GUEST: "Vieras",
                    };
                    return labels[r] || r;
                  })
                  .join(", ")}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Permissions Section */}
      {currentUser.activeAssociation?.id && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Käyttöoikeudet - {currentUser.activeAssociation?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {isAdmin
                ? "Oikeudet päivittyvät automaattisesti kun rooleja muutetaan. Muista tallentaa muutokset."
                : "Nämä ovat käyttöoikeutesi aktiivisessa tiekunnassa."}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {isAdmin ? (
              <UserPermissionsView
                associationId={currentUser.activeAssociation.id}
                permissions={previewPermissions}
                isLoading={previewLoading}
              />
            ) : (
              <UserPermissionsView
                associationId={currentUser.activeAssociation.id}
                userId={currentUser.id}
              />
            )}
          </CardContent>
        </Card>
      )}

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

export default SettingsPage;
